/* eslint-disable max-lines */
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useToggleable } from '@stores/toggleables';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { CHECKOUT_SUBSCRIPTION_TYPE_SELECTED } from '@cont/Checkout/constants/segment';
import { segment } from '@actions/analytics';
import { SUBSCRIPTION_PLAN_CLICKED } from '@constants/segment';
import { entityContentType } from '@constants/index';
import pushRoute from '@utils/pushRoute';
import { commasIndianFormat, currencySymbolMap } from '@utils/textHelper';
import useSubsriptionFeatures from '@api/hooks/goal/useSubscriptionBenefits';
import styled from '@emotion/styled';
import Image from '@base/Image';
import Typography from 'aquilla/core/atoms/Typography';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import useTopology from '@api/hooks/topology/useTopology';
import { getIsPrimeEnabled } from '@api/selectors/topology';
import Button from 'aquilla/core/molecules/Button';
import Iconic from 'aquilla/icons/subscriptions/Iconic';
import Plus from 'aquilla/icons/subscriptions/Plus';
import Lite from 'aquilla/icons/subscriptions/Lite';
import TopEducators from '@icon/TopEducators';
import Syllabus from 'aquilla/icons/subscriptions/Syllabus';
import BestInClass from 'aquilla/icons/subscriptions/BestInClass';
import LMP from 'aquilla/icons/subscriptions/LMP';
import { plusSubscriptionTiles } from '@cont/NewPreSubscribed/constants';
import CheckContained from 'aquilla/icons/misc/CheckContained';
import isEmptyObject from '@utils/isEmptyObject';
import SubscriptionTilesComponent from '../SubscriptionTiles';
import { getSubcriptionTypeCount } from './utils';

const Container = styled.div`
  margin: ${Spacings.SPACING_12B} auto;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const GridItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-left: 1px solid var(--color-i-divider);
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_1B};

  &.label {
    border-bottom: 1px solid var(--color-i-divider) !important;
    padding-bottom: ${Spacings.SPACING_6B};
  }

  &.footer {
    border-bottom: 1px solid var(--color-i-divider) !important;
    border-top: 1px solid var(--color-i-divider) !important;
    padding: ${Spacings.SPACING_6B} 0 ${Spacings.SPACING_8B};
  }

  .styledTypo {
    font-weight: ${Fonts.BOLD};
  }
`;

const Grid = styled.div`
  width: var(--layout-width);
  margin: auto;
  display: grid;

  &[aria-colcount='3'] {
    ${GridItem} {
      :nth-of-type(3n + 1) {
        justify-content: flex-start;
        border: none;
      }
    }
    grid-template-columns: repeat(3, 1fr);
  }
  &[aria-colcount='4'] {
    ${GridItem} {
      :nth-of-type(4n + 1) {
        justify-content: flex-start;
        border: none;
      }
    }
    grid-template-columns: repeat(4, 1fr);
  }
  &[aria-colcount='5'] {
    ${GridItem} {
      :nth-of-type(5n + 1) {
        justify-content: flex-start;
        border: none;
      }
    }
    grid-template-columns: repeat(5, 1fr);
  }

  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_8B};
  }
`;

const BenefitPriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacings.SPACING_4B};
`;

const StyledLabelButton = styled(Button)`
  background-color: transparent;
  border: none;
  padding: 0;
  height: ${Spacings.SPACING_8B};
  color: ${({ $color }) => $color};
  font-size: ${Spacings.SPACING_6B};
  cursor: alias;
  pointer-events: none;

  ${mediaQuery.uptoMobile} {
    font-size: initial;
    height: ${Spacings.SPACING_10B};
    width: 100%;
    cursor: initial;
    pointer-events: initial;
    &.selected {
      background-color: var(--color-i-white);
    }
    .button--right-icon {
      display: inline-flex !important;
    }
  }
`;

const StyledFreeButton = styled(Button)`
  width: 141px;
  ${mediaQuery.uptoTablet} {
    width: unset;
  }
  ${mediaQuery.uptoMobile} {
    width: 141px;
  }
`;

const StyledButton = styled(Button)`
  width: 150px;
  background-color: ${(props) => props.color};
  color: var(--color-i-white);
  border: none;

  :hover {
    background-color: ${(props) => props.color};
  }

  ${mediaQuery.uptoTablet} {
    width: unset;
  }

  ${mediaQuery.uptoMobile} {
    width: 150px;
  }
`;

const TileContainer = styled.div`
  margin: ${Spacings.SPACING_10B} auto ${Spacings.SPACING_16B};
  width: var(--layout-width);
  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_8B};
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${Spacings.SPACING_4B};
    margin: ${Spacings.SPACING_6B} auto ${Spacings.SPACING_5B};
  }
`;
const TilesHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_2B};
`;

const TilesSubHeader = styled.div`
  margin-bottom: ${Spacings.SPACING_10B};
`;

const StyledTypography = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
`;

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: ${Spacings.SPACING_2B};
`;

const PrimeBenefitText = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_1B};
`;

const PrimeLogo = styled(Image)`
  height: 20px;
  width: auto;
`;

const tileImageMapping = [TopEducators, Syllabus, BestInClass, LMP];

const NewSubscriptionBenefits = (props) => {
  const { data: cardData, goalName } = props;
  const isIconicAvailable = cardData.iconicLowestPrice !== 0;
  const isPlusAvailable = cardData.plusLowestPrice !== 0;
  const isLiteAvailable = cardData.liteLowestPrice !== 0;
  const {
    query: { goalSlug, goalUID }
  } = useRouter();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const goalSegmentData = useGoalEventProps({});
  const [typeSelected, setTypeSelected] = useState();

  const getSubscriptionRedirectHandler = (type) => {
    segment.track(CHECKOUT_SUBSCRIPTION_TYPE_SELECTED, {
      ...goalSegmentData,
      subscription_sub_type: 'Standard'
    });
    segment.track(SUBSCRIPTION_PLAN_CLICKED, {
      ...goalSegmentData,
      subscription_sub_type: 'Standard',
      entity_content_type: entityContentType.TOPOLOGY
    });
    const route = {
      href: `/goal/[goalSlug]/[goalUID]/subscribe?plan_type=${type}`,
      as: `/goal/${goalSlug}/${goalUID}/subscribe?plan_type=${type}`
    };
    pushRoute(route.href, route.as);
  };

  const getSubscription = (type) => {
    if (isLoggedIn) {
      getSubscriptionRedirectHandler(type);
    } else {
      setTypeSelected(type);
      openLoginDrawer();
    }
  };

  useEffect(() => {
    if (isLoggedIn && typeSelected && typeSelected !== 'offline') {
      getSubscriptionRedirectHandler(typeSelected);
      setTypeSelected(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const { subscriptionData } = useSubsriptionFeatures(goalUID);
  const { topology: goalTopology } = useTopology(goalUID);
  const isPrimeEnabled = getIsPrimeEnabled(goalTopology);

  if (isEmptyObject(subscriptionData) || !subscriptionData) return null;
  const currency = currencySymbolMap();
  const count = getSubcriptionTypeCount({
    isIconicAvailable,
    isLiteAvailable,
    isPlusAvailable,
    isCentreAvailable: false
  });
  return (
    <Container>
      {count < 3 && (
        <TileContainer>
          <TilesHeader>
            <Plus height="32" width="32" />
            <Typography variant="h2" color="var(--color-i-black)">
              Plus subscription
            </Typography>
          </TilesHeader>
          <TilesSubHeader>
            <Typography variant="p2" color="var(--color-i-gray-13)">
              Ace your preparation with study material prepared by top educators
            </Typography>
          </TilesSubHeader>
          <SubscriptionTilesComponent
            tiles={plusSubscriptionTiles}
            tileImageMapping={tileImageMapping}
            goalName={goalName}
          />
        </TileContainer>
      )}
      <Grid
        className="label-wrapper"
        aria-colcount={count > 2 ? count : 1 + count}
      >
        <GridItem className="label">
          <Typography variant="h4">Benefits</Typography>
        </GridItem>
        {count < 3 && (
          <GridItem className="label">
            <StyledLabelButton label="Free" $color="#3C4852" />
          </GridItem>
        )}

        {isLiteAvailable && (
          <GridItem className="label">
            <StyledLabelButton
              label="Lite"
              startIcon={<Lite height={32} width={32} />}
              $color="#1DAFDE"
            />
          </GridItem>
        )}
        {isPlusAvailable && (
          <GridItem className="label">
            <StyledLabelButton
              label="Plus"
              startIcon={<Plus height={32} width={32} />}
              $color="#5991BB"
            />
          </GridItem>
        )}
        {isIconicAvailable && (
          <GridItem className="label">
            <StyledLabelButton
              label="Iconic"
              startIcon={<Iconic height={32} width={32} />}
              $color="#EF953E"
            />
          </GridItem>
        )}
      </Grid>
      <Grid
        className="table-wrapper"
        aria-colcount={count > 2 ? count : 1 + count}
      >
        {/* All benefits of Prime row - hardcoded at the top */}
        {isPrimeEnabled && (
          <>
            <GridItem>
              <PrimeBenefitText>
                <Typography variant="h5">All benefits of </Typography>
                <PrimeLogo
                  src="prime/prime-logo.svg"
                  alt="Prime"
                  width={63}
                  height={20}
                />
              </PrimeBenefitText>
            </GridItem>
            {count < 3 && (
              <GridItem>
                <CheckContained width={20} height={20} color="#3C4852" />
              </GridItem>
            )}
            {isLiteAvailable && (
              <GridItem>
                <CheckContained width={20} height={20} color="#25B2DF" />
              </GridItem>
            )}
            {isPlusAvailable && (
              <GridItem>
                <CheckContained width={20} height={20} color="#5991BB" />
              </GridItem>
            )}
            {isIconicAvailable && (
              <GridItem>
                <CheckContained width={20} height={20} color="#FFAD3B" />
              </GridItem>
            )}
          </>
        )}

        {subscriptionData.features.map(
          ({ name, isIconic, isPlus, isLite, isFree }) => (
            <>
              <GridItem>
                <Typography variant="h5">{name}</Typography>
              </GridItem>
              {count < 3 && (
                <GridItem>
                  {isFree ? (
                    <CheckContained width={20} height={20} color="#3C4852" />
                  ) : (
                    <Image src="Group.svg" width={20} height={20} alt="icon" />
                  )}
                </GridItem>
              )}

              {isLiteAvailable && (
                <GridItem>
                  {isLite || isFree ? (
                    <CheckContained width={20} height={20} color="#25B2DF" />
                  ) : (
                    <Image src="Group.svg" width={20} height={20} alt="icon" />
                  )}
                </GridItem>
              )}
              {isPlusAvailable && (
                <GridItem>
                  {isPlus || isLite || isFree ? (
                    <CheckContained width={20} height={20} color="#5991BB" />
                  ) : (
                    <Image src="Group.svg" width={20} height={20} alt="icon" />
                  )}
                </GridItem>
              )}
              {isIconicAvailable && (
                <GridItem>
                  {isIconic || isPlus || isLite || isFree ? (
                    <CheckContained width={20} height={20} color="#FFAD3B" />
                  ) : (
                    <Image src="Group.svg" width={20} height={20} alt="icon" />
                  )}
                </GridItem>
              )}
            </>
          )
        )}
      </Grid>
      <Grid
        className="footer-wrapper"
        aria-colcount={count > 2 ? count : 1 + count}
      >
        <GridItem className="footer" />
        {count < 3 && (
          <GridItem className="footer">
            {!isLoggedIn ? (
              <StyledFreeButton
                variant="secondary"
                label="Try free"
                onClick={openLoginDrawer}
              />
            ) : (
              <Typography variant="h6" color="var(--color-i-black)">
                You’re on free plan
              </Typography>
            )}
          </GridItem>
        )}

        {isLiteAvailable && (
          <GridItem className="footer">
            <BenefitPriceWrapper>
              <PriceWrapper>
                <StyledTypography variant="p2" color="var(--color-i-gray)">
                  Starts from
                </StyledTypography>
                <Typography className="styledTypo" variant="h5" color="#25B2DF">
                  {`${currency} ${commasIndianFormat(
                    Math.round(cardData.liteLowestPrice)
                  )}`}{' '}
                  / month{' '}
                </Typography>
              </PriceWrapper>

              <StyledButton
                variant="secondary"
                color="#25B2DF"
                label="Get Lite"
                onClick={() => getSubscription('lite')}
              />
            </BenefitPriceWrapper>
          </GridItem>
        )}
        {isPlusAvailable && (
          <GridItem className="footer">
            <BenefitPriceWrapper>
              <PriceWrapper>
                <StyledTypography variant="p2" color="var(--color-i-gray)">
                  Starts from
                </StyledTypography>
                <Typography className="styledTypo" variant="h5" color="#5F8AAC">
                  {`${currency} ${commasIndianFormat(
                    Math.round(cardData.plusLowestPrice)
                  )}`}{' '}
                  / month{' '}
                </Typography>
              </PriceWrapper>

              <StyledButton
                variant="secondary"
                color="#5F8AAC"
                label="Get Plus"
                onClick={() => getSubscription('plus')}
              />
            </BenefitPriceWrapper>
          </GridItem>
        )}
        {isIconicAvailable && (
          <GridItem className="footer">
            <BenefitPriceWrapper>
              <PriceWrapper>
                <StyledTypography variant="p2" color="var(--color-i-gray)">
                  Starts from
                </StyledTypography>
                <Typography className="styledTypo" variant="h5" color="#FFAD3B">
                  {`${currency} ${commasIndianFormat(
                    Math.round(cardData.iconicLowestPrice)
                  )}`}{' '}
                  / month{' '}
                </Typography>
              </PriceWrapper>

              <StyledButton
                variant="secondary"
                color="#EF953E"
                label="Get Iconic"
                onClick={() => getSubscription('iconic')}
              />
            </BenefitPriceWrapper>
          </GridItem>
        )}
      </Grid>
    </Container>
  );
};

export default NewSubscriptionBenefits;
